import React, { useEffect, useState } from "react";
import { Images, Trash2 } from "lucide-react";

import EmptyState from "../../../../components/feedback/EmptyState";
import AlbumGalleryModal from "../../components/AlbumGalleryModal";

import { useCreatorAlbums } from "../hooks/useAlbums";

import "./CreatorAlbumPhotosView.css";

export default function CreatorAlbumPhotosView({
  username,
  albumId,
  showMenu = true,
}) {
  const { album, loading, error, getCreatorAlbum, deleteCreatorAlbumImage } =
    useCreatorAlbums(username);

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);

  useEffect(() => {
    setSelectedPhoto(null);

    if (username && albumId) {
      getCreatorAlbum(albumId);
    }
  }, [username, albumId]);

  const photos = Array.isArray(album?.images) ? album.images : [];

  const errorMessage = typeof error === "string" ? error : error?.message || "";

  const handleDeletePhoto = async (imageId) => {
    if (!imageId || deletingPhotoId !== null) return;

    if (!window.confirm("Are you sure you want to delete this photo?")) {
      return;
    }

    try {
      setDeletingPhotoId(imageId);

      const result = await deleteCreatorAlbumImage(albumId, imageId);

      if (result === false || result?.success === false) return;

      setSelectedPhoto(null);
    } catch (deleteError) {
      console.error("Unable to delete album photo:", deleteError);
    } finally {
      setDeletingPhotoId(null);
    }
  };

  if (loading && !album) {
    return (
      <div className="creator-album-photos__loading">Loading photos...</div>
    );
  }

  if (!photos.length) {
    return (
      <EmptyState
        icon={<Images size={28} />}
        title={errorMessage ? "Unable to load photos" : "No photos yet"}
        description={errorMessage || "This album does not contain any photos."}
      />
    );
  }

  return (
    <div className="creator-album-photos">
      {errorMessage && (
        <div className="creator-album-photos__error" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="creator-album-photos__grid">
        {photos.map((photo, index) => (
          <div
            key={photo.imageId ?? photo.imageTag ?? index}
            className="creator-album-photos__item"
          >
            <button
              type="button"
              className="creator-album-photos__image"
              onClick={() => setSelectedPhoto(index)}
              disabled={deletingPhotoId !== null}
              aria-label={`View photo ${index + 1}`}
            >
              <img
                src={photo.imageUrl || photo.signedUrl || ""}
                alt={photo.title || `Photo ${index + 1}`}
                loading="lazy"
                decoding="async"
              />
            </button>

            {showMenu && (
              <button
                type="button"
                className="creator-album-photos__delete"
                onClick={() => handleDeletePhoto(photo.imageId)}
                disabled={deletingPhotoId !== null}
                aria-label={`Delete photo ${index + 1}`}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      {selectedPhoto !== null && (
        <AlbumGalleryModal
          open
          images={photos}
          initialIndex={selectedPhoto}
          title={album?.title || "Album Gallery"}
          description={album?.description || album?.caption || ""}
          brandName={album?.brandName}
          username={album?.username}
          avatarUrl={album?.avatarUrl}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </div>
  );
}
